import { create } from "zustand";

import { createBillboardApi } from "../services/billboard-api/createBillboardApi";
import type {
  BillboardCreativeInput,
  BillboardQuote,
  BillboardReportInput,
  BillboardSlot,
  BillboardSlotId,
} from "../services/billboard-api/BillboardApi";

export type BillboardPurchaseState =
  | "editing"
  | "creating"
  | "payment"
  | "processing"
  | "success"
  | "stale"
  | "failure";

type BillboardStore = {
  mode: "mock" | "netlify";
  enabled: boolean;
  slots: BillboardSlot[];
  serverOffsetMs: number;
  loading: boolean;
  error: string | null;
  modalOpen: boolean;
  selectedSlotId: BillboardSlotId | null;
  quote: BillboardQuote | null;
  purchaseState: BillboardPurchaseState;
  resultMessage: string | null;
  reportMessage: string | null;
  initialize: () => void;
  shutdown: () => void;
  refresh: () => Promise<void>;
  open: (slotId: BillboardSlotId) => void;
  close: () => void;
  resetPurchase: () => void;
  requestQuote: (input: BillboardCreativeInput) => Promise<void>;
  paymentConfirmed: () => Promise<void>;
  setPaymentError: (message: string) => void;
  report: (input: BillboardReportInput) => Promise<void>;
};

const billboardService = createBillboardApi();
const billboardApi = billboardService.api;
let pollTimer: number | null = null;
let runtimeInitialized = false;

function messageFor(error: unknown): string {
  return error instanceof Error
    ? error.message
    : "The billboard service is unavailable.";
}

function wait(milliseconds: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}

export const useBillboardStore = create<BillboardStore>((set, get) => ({
  mode: billboardService.mode,
  enabled: true,
  slots: [],
  serverOffsetMs: 0,
  loading: false,
  error: null,
  modalOpen: false,
  selectedSlotId: null,
  quote: null,
  purchaseState: "editing",
  resultMessage: null,
  reportMessage: null,

  initialize: () => {
    if (runtimeInitialized) return;
    runtimeInitialized = true;
    void get().refresh();
    pollTimer = window.setInterval(() => {
      if (!document.hidden) void get().refresh();
    }, 15_000);
    document.addEventListener("visibilitychange", refreshWhenVisible);
  },

  shutdown: () => {
    runtimeInitialized = false;
    if (pollTimer !== null) window.clearInterval(pollTimer);
    pollTimer = null;
    document.removeEventListener("visibilitychange", refreshWhenVisible);
  },

  refresh: async () => {
    if (get().slots.length === 0) set({ loading: true });
    try {
      const response = await billboardApi.getSlots();
      set({
        enabled: response.enabled,
        slots: response.slots,
        serverOffsetMs: new Date(response.serverTime).getTime() - Date.now(),
        loading: false,
        error: null,
        ...(!response.enabled
          ? {
              modalOpen: false,
              selectedSlotId: null,
              quote: null,
              purchaseState: "editing" as const,
            }
          : {}),
      });
    } catch (error) {
      set({ loading: false, error: messageFor(error) });
    }
  },

  open: (slotId) =>
    set({
      modalOpen: true,
      selectedSlotId: slotId,
      quote: null,
      purchaseState: "editing",
      resultMessage: null,
      reportMessage: null,
    }),

  close: () => set({ modalOpen: false }),

  resetPurchase: () =>
    set({
      quote: null,
      purchaseState: "editing",
      resultMessage: null,
    }),

  requestQuote: async (input) => {
    set({
      purchaseState: "creating",
      quote: null,
      resultMessage: null,
    });
    try {
      const quote = await billboardApi.createPaymentIntent(input);
      set({
        quote,
        purchaseState:
          billboardService.mode === "mock" ? "processing" : "payment",
      });
      if (billboardService.mode === "mock") {
        await pollPlacement(quote);
      }
    } catch (error) {
      set({
        purchaseState: "failure",
        resultMessage: messageFor(error),
      });
    }
  },

  paymentConfirmed: async () => {
    const quote = get().quote;
    if (!quote) return;
    set({
      purchaseState: "processing",
      resultMessage:
        "Payment authorized. Waiting for the billboard to go live…",
    });
    await pollPlacement(quote);
  },

  setPaymentError: (resultMessage) =>
    set({ purchaseState: "failure", resultMessage }),

  report: async (input) => {
    set({ reportMessage: "Sending report…" });
    try {
      await billboardApi.report(input);
      set({ reportMessage: "Report received. Thank you." });
    } catch (error) {
      set({ reportMessage: messageFor(error) });
    }
  },
}));

function refreshWhenVisible(): void {
  if (!document.hidden) void useBillboardStore.getState().refresh();
}

async function pollPlacement(quote: BillboardQuote): Promise<void> {
  const deadline = Math.min(
    Date.now() + 60_000,
    Math.max(Date.now() + 10_000, new Date(quote.quoteExpiresAt).getTime()),
  );
  while (Date.now() < deadline) {
    try {
      const status = await billboardApi.getStatus(quote.placementId);
      if (status.status === "active") {
        await useBillboardStore.getState().refresh();
        useBillboardStore.setState({
          purchaseState: "success",
          resultMessage: "Your takeover is live on the road.",
        });
        return;
      }
      if (
        status.status === "replaced" ||
        (status.status === "canceled" && status.failureReason === "stale_quote")
      ) {
        await useBillboardStore.getState().refresh();
        useBillboardStore.setState({
          purchaseState: "stale",
          resultMessage:
            "Another driver took this slot first. Your authorization was canceled.",
        });
        return;
      }
      if (
        ["failed", "canceled", "refunded", "disputed", "voided"].includes(
          status.status,
        )
      ) {
        await useBillboardStore.getState().refresh();
        useBillboardStore.setState({
          purchaseState: "failure",
          resultMessage:
            status.failureReason ??
            "The takeover did not activate. No completed charge was kept.",
        });
        return;
      }
    } catch (error) {
      useBillboardStore.setState({ resultMessage: messageFor(error) });
    }
    await wait(1_000);
  }
  await useBillboardStore.getState().refresh();
  useBillboardStore.setState({
    purchaseState: "stale",
    resultMessage:
      "Activation is taking longer than expected. Check the billboard again shortly.",
  });
}
